import React from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import Empty from "../../assets/images/appImages/img-service.png";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { useLazyFavoriteByUserQuery } from "../../utils/api/favories/favorie.api";
import { ApiBaseUrl, VendeurApikey } from "../../utils/http";
import { useGetVendeurByApiKeyQuery } from "../../utils/api/product/product.api";
import { formatCurrency, getImage, getValidationRoute } from "../../utils/Utils";
import { Color } from "../../utils/theme";
import type { Product } from "../../utils/api/product/product.type";
import { addCartProduct, getCartCount, getTotalAmount, CartItemType } from "../../redux/slice/useCartSlice";
import axios from "axios";

interface ServiceCardProps {
  item: Product;
  onReserve?: (service: Product) => void;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({ item, onReserve }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((s) => s?.user);
  const { data: vendeur } = useGetVendeurByApiKeyQuery(VendeurApikey);
  const devise = (vendeur?.devise as string) || "XOF";
const { cartItems } = useAppSelector((state) => state.cart);
  const [findBySlug, { data: favorisData, isLoading }] =
    useLazyFavoriteByUserQuery();

  React.useEffect(() => {
    if (user?.slug) {
      findBySlug(user?.slug as string);
    }
  }, [user?.slug, findBySlug]);

  const config = { headers: { Authorization: `Bearer ${token}` } };

  const isFavorite = (id: number | string): boolean =>
    !!favorisData?.find((x) => x?.produit?.id == id);

  const handleAddFavoris = (id: number | string) => {
    const favorie = favorisData?.find((x) => x?.produit?.id == id);
    const fd = new FormData();
    fd.append("user", String(user?.id ?? ""));
    fd.append("produit", String(id));

    if (favorie) {
      axios
        .delete(ApiBaseUrl + `/api/favoris/${favorie?.slug}`, config)
        .then(() => findBySlug(user?.slug as string))
        .catch(() => {});
    } else {
      axios
        .post(ApiBaseUrl + "/api/favoris/", fd, config)
        .then(() => findBySlug(user?.slug as string))
        .catch(() => {});
    }
  };

  const onToggleFavorite = (id: number | string) => {
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: "Veuillez vous connecter pour ajouter un service aux favoris.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
    } else {
      handleAddFavoris(id);
    }
  };

  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : item?.variations?.[0]?.images?.[0]?.image ?? null;

  type PromotionWithActive = { taux?: number; is_active?: boolean };
  const catPromo = item?.categorie?.promotion as PromotionWithActive | undefined;
  const subCatPromo = item?.sous_categorie?.promotion as PromotionWithActive | undefined;
  const reductionTaux = catPromo?.taux ?? subCatPromo?.taux ?? 0;
  const hasPromotion = !!(catPromo?.is_active || subCatPromo?.is_active) || reductionTaux > 0;
  const prixFinal = hasPromotion
    ? item.prix - (item.prix * reductionTaux) / 100
    : item.prix;

 const handleReserverClick = () => {
  const vendeurId =
    typeof item?.vendeur === "object" && item?.vendeur !== null && "id" in item.vendeur
      ? Number((item.vendeur as { id?: string | number }).id) || 0
      : 0;

  const nouvelItem = {
    produit: item,
    prix: prixFinal,
    quantite: 1,
    stock: typeof item?.stock === "number" ? item.stock : Number(item?.stock) || 0,
    variations: [],
    vendeur: vendeurId,
    itemType: "service"  as const, 
  };

  dispatch(addCartProduct(nouvelItem));
  dispatch(getCartCount());
  dispatch(getTotalAmount());

  onReserve?.(item);
  navigate(getValidationRoute([...cartItems, nouvelItem]), { state: item });
};

  return (
    <div className="row align-items-center py-4 service-card justify-content-center">
      <div className="col-12 col-md-5 mb-3 mb-md-0">
        <img
          src={imgProd ? getImage(imgProd) : Empty}
          alt={item?.nom}
          className="img-fluid rounded-3 w-100 service-card__image"
          onError={(e) => {
            (e.target as HTMLImageElement).src = Empty;
          }}
        />
      </div>

      <div className="col-12 col-md-7 content-service">
        <h3 className="fw-bold mb-2 service-card__title">{item?.nom}</h3>
        <p className="text-muted small mb-3 service-card__description">
          {item?.description}
        </p>

        <div className="d-flex align-items-center gap-3 flex-wrap">
          <span className="fw-bold service-card__price">
            {formatCurrency(prixFinal, devise)}
          </span>

          <button
            type="button"
            className="btn btn-success rounded-3 fw-semibold px-4 service-card__btn"
            onClick={handleReserverClick}
          >
            Réserver
          </button>

          <button
            type="button"
            disabled={isLoading}
            className="btn btn-link p-0 ms-2 service-card__favorite"
            aria-label={
              isFavorite(item?.id as string)
                ? "Retirer des favoris"
                : "Ajouter aux favoris"
            }
            onClick={() => onToggleFavorite(item?.id)}
          >
            {isFavorite(item?.id as string) ? (
              <AiFillHeart size={22} color="#dc3545" />
            ) : (
              <AiOutlineHeart size={22} color="#6c757d" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};